// Quick.js - Updated with Floating Cart Icon Component
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Header from "./Header";
import { FaArrowUp, FaChevronDown, FaChevronUp, FaBarcode, FaShoppingCart } from "react-icons/fa";
import "@fortawesome/fontawesome-free/css/all.min.css";
import CrackersLoader from "./CrackersLoader";
import "./quick.css";
import MobileNavigation from "./MobileNavigation";

const Quick = () => {
  const [categories, setCategories] = useState([]);
  const [storeClose, setStoreClose] = useState(0);
  const [counts, setCounts] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 426);
  const [searchQuery, setSearchQuery] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [discountvalue, setDiscountvalue] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [discountTotal, setDiscountTotal] = useState();
  const [nonDiscountTotal, setNonDiscountTotal] = useState();
  const [expandedCategories, setExpandedCategories] = useState({});
  const qtyCount = Object.values(counts).reduce((acc, count) => acc + count, 0);
  const [showButton, setShowButton] = useState(false);
  const location = useLocation();
  const selectedCategory = location.state?.selectedCategory;
  const [selectedVideo, setSelectedVideo] = useState(null);
  const sectionRefs = useRef({});
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Calculate discount amount and final amount (needed for floating cart)
  const discountRate = Math.round(discountvalue);
  const discountAmount = (discountTotal * discountRate) / 100;
  const finalAmount = discountTotal - discountAmount + nonDiscountTotal;

  useEffect(() => {
    if (isSmallScreen) {
      const style = document.createElement('style');
      style.textContent = `
      @media (max-width: 426px) {
        .product-table table {
          width: 100% !important;
          table-layout: fixed !important;
        }
        .category-header td {
          text-align: center !important;
          justify-content: center !important;
        }
        .ctgyname {
          justify-content: center !important;
          text-align: center !important;
          width: 100% !important;
        }
      }
    `;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    if (!selectedCategory) return;

    const scrollToCategory = () => {
      const section = sectionRefs.current[selectedCategory];
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setExpandedCategories(prev => ({
          ...prev,
          [selectedCategory]: true
        }));
      } else {
        setTimeout(scrollToCategory, 100);
      }
    };

    scrollToCategory();
  }, [selectedCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const count = parseInt(localStorage.getItem("cartCount")) || 0;
    setCartCount(count);

    const handleStorageChange = () => {
      const count = parseInt(localStorage.getItem("cartCount")) || 0;
      setCartCount(count);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const fetchCartItems = () => {
    const items = JSON.parse(localStorage.getItem("cartItems")) || [];
    setCartItems(items);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}quick_buy.php`);
        const { body } = response.data;

        const initialCounts = body.category
          .flatMap((category) =>
            category.product.map((product) => ({
              product_id: product.product_id,
              count: parseInt(localStorage.getItem(`count_${product.product_id}`)) || 0,
            }))
          )
          .reduce((acc, { product_id, count }) => {
            acc[product_id] = count;
            return acc;
          }, {});

        setCategories(body.category || []);
        setDiscountvalue(body.discount_value[0]?.discount_value || []);
        setStoreClose(Number(body.store_close));
        setCounts(initialCounts);
        fetchCartItems();
        
        const initialExpandedState = {};
        body.category?.forEach(category => {
          initialExpandedState[category.category_id] = false;
        });
        setExpandedCategories(initialExpandedState);
      } catch (error) {
        console.error("There was an error fetching the data!", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 426);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      fetchCartItems();
    };

    window.addEventListener("storage", handleStorageChange);
    const handleCartUpdated = () => {
      fetchCartItems();
    };
    window.addEventListener("cartUpdated", handleCartUpdated);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartUpdated", handleCartUpdated);
    };
  }, []);

  const updateTotals = useCallback(
    (counts) => {
      let discountTotal = 0;
      let nonDiscountTotal = 0;

      categories.forEach((category) => {
        category.product.forEach((product) => {
          const count = counts[product.product_id] || 0;
          const price = parseFloat(product.oldprice.replace(/[^\d.-]/g, ""));

          if (!isNaN(price)) {
            if (category.non_discount === "0") {
              discountTotal += count * price;
            } else {
              nonDiscountTotal += count * price;
            }
          }
        });
      });

      setTotalAmount(discountTotal + nonDiscountTotal);
      setDiscountTotal(discountTotal);
      setNonDiscountTotal(nonDiscountTotal);
    },
    [categories]
  );

  useEffect(() => {
    updateTotals(counts);
  }, [counts, updateTotals]);

  const handleIncrement = (product_id, product) => {
    const newCounts = {
        ...counts,
        [product_id]: (counts[product_id] || 0) + 1,
      };
      setCounts(newCounts);
      saveCountsToLocalStorage(newCounts);
  };

  const handleDecrement = (product_id) => {
    const newCounts = {
      ...counts,
      [product_id]: Math.max((counts[product_id] || 0) - 1, 0),
    };
    setCounts(newCounts);
    saveCountsToLocalStorage(newCounts);
  };

  const saveCountsToLocalStorage = (counts) => {
    Object.entries(counts).forEach(([product_id, count]) => {
      localStorage.setItem(`count_${product_id}`, count);
    });

    const addedItems = categories
      .flatMap((category) =>
        category.product.map((product) => ({
          ...product,
          count: counts[product.product_id] || 0,
          category_id: category.category_id,
          is_non_discount: product.is_non_discount,
        }))
      )
      .filter((item) => item.count > 0);

    localStorage.setItem("cartItems", JSON.stringify(addedItems));

    const totalUniqueProductCount = Object.keys(counts).filter(
      (key) => counts[key] > 0
    ).length;
    localStorage.setItem("cartCount", totalUniqueProductCount);

    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleAddClick = (product_id, product) => {
    handleIncrement(product_id, product);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const filteredCategories = categories
    .map((category) => {
      const filteredProducts = category.product.filter((product) =>
        product.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.product_code && product.product_code.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      return {
        ...category,
        product: filteredProducts,
      };
    })
    .filter((category) => category.product.length > 0);

  const handleImageClick = (imageSrc, youtubeUrl, name) => {
    let embedUrl = null;

    if (youtubeUrl) {
      const videoIdMatch = youtubeUrl.match(/(?:shorts\/|watch\?v=)([a-zA-Z0-9_-]+)/);
      if (videoIdMatch && videoIdMatch[1]) {
        embedUrl = `https://www.youtube.com/embed/${videoIdMatch[1]}`;
      }
    }

    setSelectedImage(imageSrc);
    setSelectedName(name);
    setSelectedVideo(embedUrl);
    setShowModal(true);
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      localStorage.setItem("cartCount", cartItems.length);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [cartItems]);

  if (isLoading) {
    return <CrackersLoader />;
  }

  return (
    <div className="main quick-bottom-gap">
      <Header />

      <h5 className="minimum-order">Minimum Order Tamilnadu Rs:3000 and Other States Rs:5000</h5>
      <div className="search-box-container">
        <div className="search-box">
          <Form.Control
            type="text"
            placeholder="Search Products by Name or Code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <i className="fa fa-search"></i>
        </div>
      </div>

      <div className="product-table">
        <Table striped bordered hover className="mb-md-50">
          <thead style={{ "--bs-table-bg": "#ffc800", "--bs-table-color": "white" }}>
            <tr>
              <th className="image-column">Image</th>
              {!isSmallScreen && <th className="code-column">Code</th>}
              <th className="name-column">Product Name</th>
              <th className="name-column-small-scrn">Product Particulars</th>
              {!isSmallScreen && <th className="qty-per-box-column">Qty per Box</th>}
              {!isSmallScreen && <th className="price-column">Price</th>}
              {!isSmallScreen && storeClose === 0 && <th className="qty-column">Qty</th>}
              {!isSmallScreen && storeClose === 0 && <th className="total-column">Total</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <React.Fragment key={category.category_id}>
                  <tr 
                    className="category-header clickable"
                    onClick={() => toggleCategory(category.category_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td colSpan={isSmallScreen ? 4 : (storeClose === 0 ? 8 : 6)} className="category-header">
                      <div className="d-flex align-items-center position-relative">
                        <div className="mx-auto text-center">
                          <h5
                            className="ctgyname mb-0"
                            ref={(el) => {
                              sectionRefs.current[category.category_name] = el;
                            }}
                          >
                            {category.category_name}
                            <span className="category-count ms-2">
                              ({category.product.length} products)
                            </span>
                          </h5>
                        </div>
                        <div className="category-toggle-icon position-absolute end-0">
                          {expandedCategories[category.category_id] ? (
                            <FaChevronUp className="text-white" />
                          ) : (
                            <FaChevronDown className="text-white" />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  
                  {expandedCategories[category.category_id] && category.product.map((product) => (
                    <tr key={product.product_id} className="category-product-row">
                      <td className="tab-img image-column">
                        {product.image && (
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <img
                              className="product-img-quick"
                              src={product.image}
                              alt={product.product_name}
                              style={{
                                width: "70px",
                                height: "70px",
                                objectFit: "contain",
                                cursor: "pointer"
                              }}
                              loading="lazy"
                              onClick={() =>
                                handleImageClick(product.image, product.youtube_url, product.product_name)
                              }
                            />
                            {product.youtube_url && (
                              <span
                                style={{
                                  position: "absolute",
                                  bottom: "3px",
                                  right: "3px",
                                  padding: "19px",
                                  cursor: "pointer",
                                }}
                                title="Watch Video"
                                onClick={() => handleImageClick(product.image, product.youtube_url)}
                              >
                                <i className="bi bi-play-circle"
                                  style={{
                                    color: "white",
                                    fontSize: "22px",
                                    backgroundColor: "red",
                                    borderRadius: "50%",
                                    paddingTop: "0px",
                                    paddingBottom: "0px",
                                    paddingRight: "6px",
                                    paddingLeft: "6px",
                                    objectFit: "contain"
                                  }}
                                ></i>
                              </span>
                            )}
                            {isSmallScreen && product.product_code && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "3px",
                                  left: "3px",
                                  background: "rgba(0,0,0,0.7)",
                                  color: "white",
                                  fontSize: "10px",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontWeight: "bold"
                                }}
                              >
                                <FaBarcode size={10} style={{ marginRight: "3px" }} />
                                {product.product_code}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {!isSmallScreen && (
                        
                        <td className="code-column">
                            {product.product_code ? (
                              <span className="badge bg-info text-dark" style={{ fontSize: "12px" }}>
                                <FaBarcode size={12} className="me-1" />
                                {product.product_code}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                            )}

                      <td className="product-detail-column name-column">
                        <div className="product-detail">
                          <strong className="productname">
                            {product.product_name}
                          </strong>
                        </div>
                      </td>
                      <td className="product-detail-column name-column-small-scrn">
                        <div className="product-detail">
                          <strong className="productname">
                            {product.product_name}
                          </strong>
                          {isSmallScreen && (
                            <>
                              <div className="qty_per_box" style={{ color: "#d800ab" }}>
                                {product.qty_per_box || "N/A"}
                              </div>
                              <div>
                                {parseInt(category.non_discount, 10) === 0 ? (
                                  <>
                                    <i className="bi bi-currency-rupee"></i>
                                    <del style={{ color: "red" }}>
                                      {product.oldprice
                                        ? parseFloat(product.oldprice.replace(/[^\d.-]/g, "")).toFixed(2)
                                        : "N/A"}
                                    </del>
                                    <nobr style={{ color: "green" }}>
                                      <i className="bi bi-currency-rupee"></i>
                                      <b>
                                        {product.price
                                          ? parseFloat(product.price.replace(/[^\d.-]/g, "")).toFixed(2)
                                          : "N/A"}
                                      </b>
                                    </nobr>
                                  </>
                                ) : (
                                  <>
                                    <nobr style={{ color: "green" }}>
                                      <i className="bi bi-currency-rupee"></i>
                                      <b>
                                        {product.oldprice
                                          ? parseFloat(product.oldprice.replace(/[^\d.-]/g, "")).toFixed(2)
                                          : "N/A"}
                                      </b>
                                    </nobr>
                                  </>
                                )}
                              </div>
                              {counts[product.product_id] === 0 ? (
                                storeClose === 0 && (
                                  <Button
                                    onClick={() => handleAddClick(product.product_id, product)}
                                    className="add-btn"
                                  >
                                    Add
                                  </Button>
                                )
                              ) : (
                                storeClose === 0 && (
                                  <div className="counter-btn">
                                    <Button
                                      className="countadd"
                                      onClick={() => handleDecrement(product.product_id)}
                                    >
                                      <b>-</b>
                                    </Button>
                                    <div className="counter-display">
                                      {counts[product.product_id]}
                                    </div>
                                    <Button
                                      className="countadd"
                                      onClick={() => handleIncrement(product.product_id, product)}
                                    >
                                      <b>+</b>
                                    </Button>
                                    &nbsp;&nbsp;&nbsp;
                                    {parseInt(category.non_discount, 10) === 0 ? (
                                      <>
                                        Total :
                                        <i className="bi bi-currency-rupee"></i>
                                        <b>
                                          {(
                                            Math.round(
                                              counts[product.product_id] *
                                              ((parseFloat(product.price.replace(/[^\d.-]/g, "")) || 0).toFixed(2)) *
                                              100
                                            ) / 100
                                          ).toFixed(2)}
                                        </b>
                                      </>
                                    ) : (
                                      <>
                                        Total :
                                        <i className="bi bi-currency-rupee"></i>
                                        {Math.round(
                                          counts[product.product_id] *
                                          (parseFloat(product.oldprice.replace(/[^\d.-]/g, "")) || 0) *
                                          100
                                        ) / 100}
                                      </>
                                    )}
                                  </div>
                                )
                              )}
                            </>
                          )}
                        </div>
                      </td>

                      {!isSmallScreen && (
                        <>
                          <td className="qty-per-box-column" style={{ color: "#d800ab" }}>
                            {product.qty_per_box || "N/A"}
                          </td>
                          <td className="price-column">
                            {parseInt(category.non_discount, 10) === 0 ? (
                              <>
                                <i className="bi bi-currency-rupee"></i>
                                <del style={{ color: "red" }}>
                                  {product.oldprice
                                    ? parseFloat(product.oldprice.replace(/[^\d.-]/g, "")).toFixed(2)
                                    : "N/A"}
                                </del>
                                <nobr style={{ color: "green" }}>
                                  <i className="bi bi-currency-rupee"></i>
                                  <b>
                                    {product.price
                                      ? parseFloat(product.price.replace(/[^\d.-]/g, "")).toFixed(2)
                                      : "N/A"}
                                  </b>
                                </nobr>
                              </>
                            ) : (
                              <>
                                <nobr style={{ color: "green" }}>
                                  <i className="bi bi-currency-rupee"></i>
                                  <b>
                                    {product.oldprice
                                      ? parseFloat(product.oldprice.replace(/[^\d.-]/g, "")).toFixed(2)
                                      : "N/A"}
                                  </b>
                                </nobr>
                              </>
                            )}
                          </td>
                          {storeClose === 0 && (
                            <td className="qty-column">
                              { counts[product.product_id] === 0 ? (
                                storeClose === 0 && (
                                  <Button
                                    onClick={() => handleAddClick(product.product_id, product)}
                                    className="add-btn glossy-add"
                                  >
                                    Add
                                  </Button>
                                )
                              ) : (
                                storeClose === 0 && (
                                  <div className="counter-btn">
                                    <Button
                                      className="countadd"
                                      onClick={() => handleDecrement(product.product_id)}
                                    >
                                      <b>-</b>
                                    </Button>
                                    <div className="counter-display">
                                      {counts[product.product_id]}
                                    </div>
                                    <Button
                                      className="countadd"
                                      onClick={() => handleIncrement(product.product_id, product)}
                                    >
                                      <b>+</b>
                                    </Button>
                                  </div>
                                )
                              )}
                            </td>
                          )}
                          {storeClose === 0 && (
                            <td className="total-column">
                              {parseInt(category.non_discount, 10) === 0 ? (
                                <>
                                  <i className="bi bi-currency-rupee"></i>
                                  <b>
                                    {(Math.round(
                                      counts[product.product_id] *
                                      (parseFloat(product.price.replace(/[^\d.-]/g, "")) || 0) *
                                      100
                                    ) / 100).toFixed(2)}
                                  </b>
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-currency-rupee"></i>
                                  {Math.round(
                                    counts[product.product_id] *
                                    (parseFloat(product.oldprice.replace(/[^\d.-]/g, "")) || 0) *
                                    100
                                  ) / 100}
                                </>
                              )}
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={isSmallScreen ? 4 : (storeClose === 0 ? 8 : 6)} className="text-center py-4">
                  <div className="no-products-found">
                    <i className="bi bi-search" style={{ fontSize: "2rem", color: "#6c757d", marginBottom: "10px" }}></i>
                    <p className="mb-0">No products found matching "{searchQuery}"</p>
                    <small className="text-muted">Try a different search term</small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <h6 className="mobile-msg">Continue To Purchase Click The View Cart Icon</h6>
      </div>

      {storeClose === 0 && (
        <>
          <div className="order">
            <Container fluid>
              <Row className="justify-content-center">
                <Col xs={12} sm={6} md={4} lg={3} className="mb-2 mb-sm-0">
                  <div className="col1">
                    <Link to="/cart">
                      <div className="order-label">Order Total</div>
                      <hr className="order-divider" />
                      <div className="order-value">
                        <i className="bi bi-currency-rupee"></i>
                        {totalAmount.toFixed(2)}
                      </div>
                    </Link>
                  </div>
                </Col>
                <Col xs={12} sm={6} md={4} lg={3} className="mb-2 mb-sm-0">
                  <div className="col2">
                    <Link to="/cart">
                      <div className="order-label">Discount ({discountRate}%)</div>
                      <hr className="order-divider" />
                      <div className="order-value">
                        <i className="bi bi-currency-rupee"></i>
                        {discountAmount.toFixed(2)}
                      </div>
                    </Link>
                  </div>
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <div className="col3">
                    <Link to="/cart">
                      <div className="order-label">Net Amount</div>
                      <hr className="order-divider" />
                      <div className="order-value">
                        <i className="bi bi-currency-rupee"></i>
                        {finalAmount.toFixed(2)}
                      </div>
                    </Link>
                  </div>
                </Col>
              </Row> <Link to="/cart">
              <h6 className="desktop-msg">Preview Order</h6></Link>
            </Container>
          </div>

          <div className="minifooter">
            <div className="rate">
              <div className="rate-item">
                <span className="rate-label">Discount ({discountRate}%):</span>
                <span className="rate-value">
                  <i className="bi bi-currency-rupee"></i>
                  {discountAmount.toFixed(2)}
                </span>
              </div>
              <div className="rate-item">
                <span className="rate-label">Total Qty: {qtyCount} | Total Price:</span>
                <span className="rate-value">
                  <i className="bi bi-currency-rupee"></i>
                  {finalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ========== NEW FLOATING CART ICON ========== */}
      {storeClose === 0 && cartCount > 0 && (
        <Link to="/cart" className="floating-cart-icon" aria-label="View Cart">
          <div className="floating-cart-inner">
            <FaShoppingCart className="floating-cart-svg" />
            <span className="floating-cart-badge">{cartCount}</span>
          </div>
        </Link>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="product-modal"
      >
        <Modal.Header closeButton className="modal-header-enhanced">
          <Modal.Title id="contained-modal-title-vcenter">
            {selectedName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-enhanced">
          {selectedVideo ? (
            <div className="video-container">
              <iframe
                src={selectedVideo}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <div className="image-container">
              <img
                src={selectedImage}
                alt="Selected"
                className="modal-image"
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-enhanced">
          <Button onClick={() => setShowModal(false)} className="modal-close-btn">
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {showButton && (
        <button className="scroll-to-top1" onClick={scrollToTop}>
          <FaArrowUp />
        </button>
      )}

      <div className="minifooter1">
        <div className="minimenu">
          <Link to="/home">
            <div style={{ display: "flex", flexDirection: "column", color: "white" }}>
              <i className="bi bi-house-door" style={{ marginLeft: "12px", fontSize: "1.4rem" }}></i>
              Home
            </div>
          </Link>

          <Link to="/quick">
            <div style={{ display: "flex", flexDirection: "column", color: "white" }}>
              <i className="bi bi-bag" style={{ marginLeft: "15px", fontSize: "1.4rem" }}></i>
              Enquiry
            </div>
          </Link>
          {storeClose === 0 ? (
            <Link to="/cart">
              <div style={{ display: "flex", flexDirection: "column", color: "white", position: "relative" }}>
                <i className="bi bi-cart" style={{ marginLeft: "25px", fontSize: "1.4rem", position: "relative" }}></i>
                View Cart
                {cartCount > 0 && (
                  <span className="badge" style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-2px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    padding: "2px 6px",
                    fontSize: "0.7rem",
                    color: "#a01717",
                  }}>
                    {cartCount}
                  </span>
                )}
              </div>
            </Link>
          ) : (
            <Link to="/about">
              <div style={{ display: "flex", flexDirection: "column", color: "white" }}>
                <i className="bi bi-journal-text officon" style={{ marginLeft: "20px", fontSize: "1.4rem", color: "white" }}></i>
                About Us
              </div>
            </Link>
          )}

          <Link to="/contact">
            <div style={{ display: "flex", flexDirection: "column", color: "white" }}>
              <i className="bi bi-telephone" style={{ marginLeft: "12px", fontSize: "1.4rem" }}></i>
              Contact
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Quick;